import { useEffect, useRef, useState } from 'react'
import { Avatar } from 'antd'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import reload from 'images/reload.svg'
import {
  HeaderContainer,
  HeaderWrapper,
  HeaderLeft,
  NumberButton,
  ScheduleButton,
  StyledTabs,
  HeaderRight,
  AvatarContainer,
  TabLabelWrap,
  TabCountSlot,
} from './Header.styled'
import LogoBadgeOutlined from '../../images/LogoBadgeOutlined.png'
import { setCurrentScreen } from '../../redux/flows/portal/index'
import {
  activateGhostSlot,
  deactivateGhostSlot,
  getGhostSlotStatus,
  getMemberCount,
} from '../../redux/flows/portal/studio'
import { selectShopBoardForDisplay } from '../../redux/shop/selectors'
import { RootState, useAppDispatch, useAppSelector } from '../../redux/store'
import { APIStatus } from '../../redux/types'
import { COLORS } from '../../tokens'

export default function Header() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const currentScreen = useSelector((state: RootState) => state.portal.currentScreen)
  const doctor = useAppSelector((state: RootState) => state.doctorAuth.doctor)
  const pendingNotes = useSelector((state: RootState) => state.notesPortal.notes)
  const memberCount = useSelector((state: RootState) => state.studioPortal.memberCount)
  const allMemberCount = useSelector((state: RootState) => state.studioPortal.allMemberCount)
  const allMemberCountStatus = useSelector((state: RootState) => state.studioPortal.allMemberCountStatus)
  const currentClinicId = useSelector((state: RootState) => state.studioAuth.studioDetails?.id)
  const [showAllMembers, setShowAllMembers] = useState(false)
  const ghostSlotActive = useSelector((state: RootState) => state.studioPortal.ghostSlotActive)
  const ghostSlotToggleStatus = useSelector((state: RootState) => state.studioPortal.ghostSlotToggleStatus)
  const shopOrderCount = useAppSelector((state: RootState) => selectShopBoardForDisplay(state).count)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressStartTimeRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)

  useEffect(() => {
    if (!currentClinicId) {
      return
    }

    dispatch(getGhostSlotStatus({ clinicId: currentClinicId }))

    const POLL_MS = 2000

    const interval = setInterval(() => {
      dispatch(getGhostSlotStatus({ clinicId: currentClinicId }))
    }, POLL_MS)

    return () => clearInterval(interval)
  }, [dispatch, currentClinicId])

  useEffect(() => {
    if (!currentClinicId) {
      return
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        dispatch(getGhostSlotStatus({ clinicId: currentClinicId }))
      }
    }

    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [dispatch, currentClinicId])

  const toggleGhostSlot = async () => {
    if (!currentClinicId || ghostSlotToggleStatus === APIStatus.pending) {
      return
    }

    try {
      if (ghostSlotActive) {
        await dispatch(deactivateGhostSlot({ clinicId: currentClinicId })).unwrap()
      } else {
        await dispatch(activateGhostSlot({ clinicId: currentClinicId })).unwrap()
      }
      dispatch(getGhostSlotStatus({ clinicId: currentClinicId }))
    } catch {
      dispatch(getGhostSlotStatus({ clinicId: currentClinicId }))
    }
  }

  const startLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    longPressStartTimeRef.current = Date.now()
    longPressTriggeredRef.current = false
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true
      toggleGhostSlot()
      longPressTimerRef.current = null
    }, 2000)
  }

  const clearLongPress = () => {
    const pressStart = longPressStartTimeRef.current
    const pressDuration = pressStart ? Date.now() - pressStart : 0
    // If release happens right around threshold before timer callback runs,
    // still trigger immediately so behavior matches 2-second long press.
    if (!longPressTriggeredRef.current && pressDuration >= 2000) {
      longPressTriggeredRef.current = true
      toggleGhostSlot()
    }
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    longPressStartTimeRef.current = null
  }

  const handleMemberCountClick = () => {
    if (showAllMembers) {
      setShowAllMembers(false)
      return
    }

    setShowAllMembers(true)
    if (allMemberCountStatus !== APIStatus.pending) {
      dispatch(getMemberCount({}))
    }
  }

  const displayedMemberCount = showAllMembers ? allMemberCount?.count ?? memberCount?.count : memberCount?.count

  const onScheduleClick = () => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false
      return
    }
    dispatch(setCurrentScreen('schedule'))
    navigate('/schedule')
  }

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = null
      }
    }
  }, [])

  const renderTabLabel = (name: string, count?: number) => (
    <TabLabelWrap>
      {name}
      {!!count && <TabCountSlot>{`| ${count}`}</TabCountSlot>}
    </TabLabelWrap>
  )

  const tabItems = [
    { key: 'current', label: renderTabLabel('Current') },
    { key: 'lobby', label: renderTabLabel('Lobby') },
    {
      key: 'notes',
      label: renderTabLabel('Notes', pendingNotes?.length || undefined),
    },
    {
      key: 'shop',
      label: renderTabLabel('Shop', shopOrderCount || undefined),
    },
  ]

  return (
    <HeaderContainer>
      <HeaderWrapper>
        <HeaderLeft>
          <img src={LogoBadgeOutlined} alt="K" style={{ width: '40px', height: '40px' }} />

          <NumberButton
            $active={showAllMembers}
            $isRefreshing={showAllMembers && allMemberCountStatus === APIStatus.pending}
            onClick={handleMemberCountClick}
          >
            {displayedMemberCount ?? '-'}
          </NumberButton>
          <ScheduleButton
            onMouseDown={startLongPress}
            onMouseUp={clearLongPress}
            onMouseLeave={clearLongPress}
            onTouchStart={startLongPress}
            onTouchEnd={clearLongPress}
            onTouchCancel={clearLongPress}
            onClick={onScheduleClick}
            style={{
              background: ghostSlotActive
                ? '#000000'
                : currentScreen === 'schedule'
                  ? COLORS.primary
                  : COLORS.background,
              color: ghostSlotActive
                ? '#FFFFFF'
                : currentScreen === 'schedule'
                  ? COLORS.textSecondary
                  : COLORS.textPrimary,
            }}
          >
            <span>Schedule</span>
          </ScheduleButton>
        </HeaderLeft>
        <StyledTabs
          items={tabItems}
          defaultActiveKey="current"
          activeKey={currentScreen === 'schedule' ? 'schedule' : currentScreen}
          onTabClick={(key) => {
            dispatch(setCurrentScreen(key))
            navigate(`/${key}`)
          }}
        />
        <HeaderRight>
          <AvatarContainer>
            <Avatar
              onClick={() => {
                dispatch(setCurrentScreen('doctor'))
                navigate('/doctor')
              }}
              size={50}
              src={doctor?.user?.profilePic || ''}
              style={{ border: '1px solid var(--color-border-primary)' }}
            ></Avatar>
          </AvatarContainer>
        </HeaderRight>
      </HeaderWrapper>
    </HeaderContainer>
  )
}
